import { useState } from "react";
import { Button } from "../design-system/Button";
import { Icon } from "../design-system/Icon";

export interface ConfirmOptions {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly danger?: boolean;
}
interface PendingConfirm extends ConfirmOptions { readonly resolve: (ok: boolean) => void }

/** Diálogo modal de confirmación para acciones irreversibles (Fase 5, robustez). */
function ConfirmDialog({ pending, onClose }: { pending: PendingConfirm; onClose: (ok: boolean) => void }): JSX.Element {
  return (
    <div
      role="presentation"
      onClick={() => onClose(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "var(--sp-4)" }}
    >
      <div
        role="dialog" aria-modal="true" aria-label={pending.title}
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg, 14px)", maxWidth: 440, width: "100%", padding: "var(--sp-5)", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}
      >
        <div className="row" style={{ gap: 10, marginBottom: "var(--sp-3)" }}>
          <Icon name={pending.danger ? "alert" : "spark"} size={20} style={{ color: pending.danger ? "var(--danger)" : "var(--brand)" }} />
          <h2 className="strong" style={{ fontSize: 16 }}>{pending.title}</h2>
        </div>
        <p className="muted" style={{ fontSize: 13.5, marginBottom: "var(--sp-5)", lineHeight: 1.5 }}>{pending.message}</p>
        <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
          <Button variant="ghost" size="sm" onClick={() => onClose(false)}>Cancelar</Button>
          <Button variant={pending.danger ? "danger" : "secondary"} size="sm" icon={pending.danger ? "alert" : "check"} onClick={() => onClose(true)}>
            {pending.confirmLabel ?? "Confirmar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook imperativo de confirmación: `ask(opts)` abre el diálogo y resuelve a true/false;
 * `node` debe renderizarse una vez en la pantalla.
 */
export function useConfirm(): { ask: (opts: ConfirmOptions) => Promise<boolean>; node: JSX.Element | null } {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const ask = (opts: ConfirmOptions): Promise<boolean> =>
    new Promise<boolean>((resolve) => setPending({ ...opts, resolve }));
  const close = (ok: boolean): void => {
    if (pending) pending.resolve(ok);
    setPending(null);
  };
  return { ask, node: pending ? <ConfirmDialog pending={pending} onClose={close} /> : null };
}
